import { css, styled } from '../../hooks/theme';
import { getCssFont } from '../../styles';
import { Tokens } from '../../types';

interface CheckBoxProps {
  checked?: boolean;
  disabled?: boolean;
  bordered?: boolean;
  side?: 'right' | 'left';
}

export const Container = styled.div<CheckBoxProps>`
  display: flex;
  align-items: center;
  width: fit-content;
  background: ${props => props.theme.colors.neutral.grey[0]};

  ${getCssFont(Tokens.eixods.font.desktop.paragraph.p2.regular)}

  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  border: ${props =>
    !props.bordered
      ? '1px solid none'
      : props.checked
      ? `1px solid ${props.theme.colors.brand.primary[100]}`
      : `1px solid ${props.theme.colors.neutral.grey[40]}`};
  border-radius: 4px;

  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* IE10+/Edge */
  user-select: none; /* Standard */

  ${props =>
    props.bordered &&
    !props.disabled &&
    css`
      &:hover {
        border: 1px solid ${props => props.theme.colors.neutral.grey[100]};
      }
    `}

  ${props =>
    props.disabled &&
    props.bordered &&
    css`
      border: 1px solid ${props.theme.colors.neutral.grey[40]};
    `}
  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}
`;
Container.defaultProps = {
  theme: Tokens.eixods,
};

interface PaddingProps {
  bordered?: boolean;
}
export const Padding = styled.div<PaddingProps>`
  padding: ${props => props.bordered && '12px 16px'};
`;

interface CheckBoxContainerProps extends CheckBoxProps {
  hasContent?: boolean;
}
export const CheckBoxContainer = styled.div<CheckBoxContainerProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: ${props => props.disabled && 'not-allowed'};

  & input[type='checkbox'] {
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    min-width: 24px;
    height: 24px;
    border-radius: 4px;
    border: 1px solid ${props => props.theme.colors.neutral.grey[40]};
    margin: 0;

    appearance: none;
    -webkit-appearance: none;
    z-index: 1;

    ${props =>
      props.side === 'left' &&
      css`
        order: 2;
      `}
  }

  & input[type='checkbox'] + label {
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    ${props => {
      const space = props.hasContent ? 16 : 8;
      return props.side === 'left'
        ? css`
            margin-right: ${space}px;
          `
        : css`
            margin-left: ${space}px;
          `;
    }}
  }

  & input[type='checkbox']:checked {
    border: none;
  }

  & input[type='checkbox']:checked + label::before {
    content: '';
    display: block;
    position: absolute;
    text-align: center;
    height: 24px;
    width: 24px;
    top: calc(50% - 12px);
    background: ${props => props.theme.colors.brand.primary[100]};
    border-radius: 4px;
    ${props =>
      props.side === 'left'
        ? css`
            right: 0;
          `
        : css`
            left: 0;
          `}
  }
  & input[type='checkbox']:checked + label::after {
    content: url('data:image/svg+xml; utf8, <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5L4 8L11 1" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
    color: ${props => props.theme.colors.neutral.grey[0]};
    display: block;
    position: absolute;
    top: calc(50% - 11px);
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    ${props =>
      props.side === 'left'
        ? css`
            right: 6px;
          `
        : css`
            left: 6px;
          `}
  }
`;
CheckBoxContainer.defaultProps = {
  theme: Tokens.eixods,
};

export const Label = styled.div`
  ${getCssFont(Tokens.eixods.font.desktop.paragraph.p2.regular)}
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

export const Content = styled.div`
  ${getCssFont(Tokens.eixods.font.desktop.paragraph.p3.regular)}
  word-wrap: break-word;
  overflow-wrap: break-word;
`;
